import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { IconInfoCircle, IconMapPin } from '@tabler/icons-react';
import MultiImageUpload from '../../../../../../components/common/MultiImageUpload';
import { actionTagIcons, issueIcons, scaleOptionIcons } from '../../../constants/iconMappings';
import { ISSUE_STATUSES } from '../../../constants/issueStatuses';
import LinkInput from './LinkInput';
import LinkPreview from './LinkPreview';

const SelectedItem = ({ icon, text }) => (
  <div className="selected-item">
    <span className="selected-item-icon" style={{ fontSize: 20, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {icon}
    </span>
    <span className="selected-item-text">{text}</span>
  </div>
);

const Step4Content = (props) => {
  const {
    tag, issue, selectedScale, location,
    title, setTitle, content, setContent, images, setImages,
    selectedIssueStatus, setSelectedIssueStatus,
    titleFocused, setTitleFocused, titleError, setTitleError,
    contentFocused, setContentFocused,
    link, setLink, linkError, setLinkError,
    linkAnchorEl, setLinkAnchorEl, showLinkInput, setShowLinkInput,
    linkPreview, setLinkPreview, isLoadingPreview, lastFetchedUrl,
  } = props;

  const handleRemoveLinkPreview = () => {
    setLinkPreview(null);
    setLink('');
    lastFetchedUrl.current = null;
  };

  const textFieldSx = {
    mb: 2.5,
    '& .MuiInputBase-input': {
      padding: '8px 12px',
      backgroundColor: 'white',
      border: 'none',
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: titleError ? '#f44336 !important' : (titleFocused ? '#1976d2 !important' : '#e0e0e0 !important'),
      borderRadius: '8px',
    },
  };

  return (
    <motion.div key="step4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="step-content">
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.5 }}>
        Your Selections
      </Typography>
      <Box className="selected-items" sx={{ mb: 3 }}>
        {tag && <SelectedItem icon={React.cloneElement(actionTagIcons[tag.id], { size: 20 })} text={tag.label} />}
        {issue && <SelectedItem icon={React.cloneElement(issueIcons[issue.id] || <IconInfoCircle size={20} />, { size: 20 })} text={issue.name} />}
        {selectedScale && (
          <SelectedItem
            icon={React.cloneElement(scaleOptionIcons[selectedScale] || <IconMapPin size={20} />, { size: 20 })}
            text={selectedScale === 'global' ? 'Global' : location}
          />
        )}
      </Box>

      <TextField
        placeholder="Enter a descriptive title..."
        variant="outlined"
        fullWidth
        size="small"
        value={title}
        onChange={(e) => { if (e.target.value.length <= 50) { setTitle(e.target.value); setTitleError(false); }}}
        onFocus={() => setTitleFocused(true)}
        onBlur={() => { setTitleFocused(false); setTitleError(!title.trim()); }}
        error={titleError}
        helperText={titleError ? 'Title is required' : ''}
        sx={textFieldSx}
        inputProps={{ maxLength: 50 }}
        InputProps={{
          endAdornment: (
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              {`${title.length}/50`}
            </Typography>
          ),
        }}
      />

      <Box className="postbox-modal__contentbox" sx={{ mb: 2.5 }}>
        <Box className="postbox-modal__textarea-wrapper">
          <textarea
            className="postbox-modal__textarea"
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={4}
            maxLength={25000}
            aria-label="Share your thoughts"
            onFocus={() => setContentFocused(true)}
            onBlur={() => setContentFocused(false)}
          />
          {(!content && !contentFocused) && (
            <span className="postbox-modal__placeholder">Share your thoughts and experiences...</span>
          )}
          <span className="postbox-modal__charcount">{content.length} / 25000</span>
          <Box display="flex" alignItems="center" gap={1} mt={2}>
            <LinkInput
              link={link}
              setLink={setLink}
              linkError={linkError}
              setLinkError={setLinkError}
              linkAnchorEl={linkAnchorEl}
              setLinkAnchorEl={setLinkAnchorEl}
              showLinkInput={showLinkInput}
              setShowLinkInput={setShowLinkInput}
            />
          </Box>
        </Box>
      </Box>

      <LinkPreview
        isLoadingPreview={isLoadingPreview}
        linkPreview={linkPreview}
        onRemove={handleRemoveLinkPreview}
      />

      <Box mb={2.5}>
        <MultiImageUpload
          images={images}
          onChange={setImages}
          maxImages={4}
          allowCrop={true}
          uploadFolder="user_photos/posts"
          maxSize={{ width: 1600, height: 1600 }}
          cropShape="rect"
          aspectRatio={0}
        />
      </Box>

      <Box mb={2.5}>
        <FormControl fullWidth size="small" required>
          <InputLabel id="issue-status-select-label">Select Status (required)</InputLabel>
          <Select
            labelId="issue-status-select-label"
            id="issue-status-select"
            value={selectedIssueStatus}
            label="Select Status (required)"
            onChange={(e) => setSelectedIssueStatus(e.target.value)}
            sx={{ borderRadius: '8px', bgcolor: 'white' }}
          >
            {ISSUE_STATUSES.map((status) => (
              <MenuItem key={status.id} value={status.id}>{status.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </motion.div>
  );
};

export default Step4Content;